import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { ConversationProvider } from "@elevenlabs/react";
import LanguageLayout from "@/components/LanguageLayout";
import LangRedirect from "@/components/LangRedirect";
import Analytics from "@/components/Analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Route-based code splitting
const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const HorseDetailPage = lazy(() => import("./pages/HorseDetailPage"));
const CheckoutSecurePage = lazy(() => import("./pages/CheckoutSecurePage"));
const SettlementPage = lazy(() => import("./pages/SettlementPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const HorsesPage = lazy(() => import("./pages/HorsesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TrackTransactionPage = lazy(() => import("./pages/TrackTransactionPage"));
const TransporterUpdatePage = lazy(() => import("./pages/TransporterUpdatePage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const DossierPage = lazy(() => import("./pages/DossierPage"));
const InfoPage = lazy(() => import("./pages/InfoPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const EscrowPage = lazy(() => import("./pages/EscrowPage"));
const RedirectToPaymentPage = lazy(() => import("./pages/RedirectToPaymentPage"));
const CGUPage = lazy(() => import("./pages/CGUPage"));
const CGAPage = lazy(() => import("./pages/CGAPage"));
const ReturnPolicyPage = lazy(() => import("./pages/ReturnPolicyPage"));
const RefundPolicyPage = lazy(() => import("./pages/RefundPolicyPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const SellerDashboardPage = lazy(() => import("./pages/SellerDashboardPage"));
const SellerHorseFormPage = lazy(() => import("./pages/SellerHorseFormPage"));
const HoldShiftAdminPage = lazy(() => import("./pages/HoldShiftAdminPage"));
const HoldShiftAuthPage = lazy(() => import("./pages/HoldShiftAuthPage"));
const HoldShiftDashboardPage = lazy(() => import("./pages/HoldShiftDashboardPage"));
const BlogListPage = lazy(() => import("./pages/BlogListPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const ArchivesPage = lazy(() => import("./pages/ArchivesPage"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-background flex flex-col">
    {/* Header skeleton to prevent CLS */}
    <div className="h-[72px] border-b border-border bg-background" />
    <div className="flex-1 flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ConversationProvider>
    <CurrencyProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Analytics />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LangRedirect />} />
              <Route path="/auth" element={<LangRedirect suffix="/auth" />} />
              <Route path="/account" element={<LangRedirect suffix="/account" />} />
              <Route path="/:lang" element={<LanguageLayout />}>
                <Route index element={<CatalogPage />} />
                <Route path="horses" element={<HorsesPage />} />
                <Route path="archives" element={<ArchivesPage />} />
                <Route path="sold" element={<ArchivesPage />} />
                <Route path="category/:category" element={<CategoryPage />} />
                <Route path="horse/:id" element={<HorseDetailPage />} />
                <Route path="dossier/:id" element={<DossierPage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="legal" element={<LegalPage />} />
                <Route path="contact" element={<ContactPage />} />
                <Route path="info" element={<InfoPage />} />
                <Route path="faq" element={<FAQPage />} />
                <Route path="escrow" element={<EscrowPage />} />
                <Route path="blog" element={<BlogListPage />} />
                <Route path="blog/:slug" element={<BlogPostPage />} />
                <Route path="redirect" element={<RedirectToPaymentPage />} />
                <Route path="cgu" element={<CGUPage />} />
                <Route path="cga" element={<CGAPage />} />
                <Route path="return-policy" element={<ReturnPolicyPage />} />
                <Route path="refund-policy" element={<RefundPolicyPage />} />
                <Route path="privacy" element={<PrivacyPolicyPage />} />
                <Route path="cookies" element={<CookiePolicyPage />} />
                <Route path="checkout-secure/:transactionId" element={<CheckoutSecurePage />} />
                <Route path="secure-payment/:transactionId" element={<SettlementPage />} />
              
                <Route path="track" element={<TrackTransactionPage />} />
                <Route path="transporter" element={<TransporterUpdatePage />} />
                <Route path="auth" element={<AuthPage />} />
                <Route path="account" element={<AccountPage />} />
                <Route path="change-password" element={<ChangePasswordPage />} />
                <Route path="seller" element={<SellerDashboardPage />} />
                <Route path="seller/new" element={<SellerHorseFormPage />} />
                <Route path="seller/edit/:id" element={<SellerHorseFormPage />} />
              </Route>
            <Route path="/:lang/settlement/:transactionId" element={<SettlementPage />} />
            <Route path="/:lang/settlement" element={<SettlementPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/:lang/admin" element={<AdminPage />} />
            <Route path="/holdshift" element={<HoldShiftAdminPage />} />
            <Route path="/holdshift/auth" element={<HoldShiftAuthPage />} />
            <Route path="/holdshift/dashboard" element={<HoldShiftDashboardPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </CurrencyProvider>
    </ConversationProvider>
  </QueryClientProvider>
);

export default App;
